import React, { ReactElement, useMemo } from 'react';
import { NuvoGrid } from '@nuvolo/nuux-core/components/NuvoGrid';
import DashboardItem from '#/modules/dashboard/components/DashboardItem';
import DashboardSectionHeading from '#/modules/dashboard/components/DashboardSectionHeading';
import DashboardCards from '#/modules/dashboard/components/DashboardCards';
import BarChart, { BarLineChart } from '#/modules/dashboard/components/BarChart';
import PieChart from '#/modules/dashboard/components/PieChart';
import StackedBarChart from '#/modules/dashboard/components/StackedBarChart';
import { AssetPlan, DashboardStatsPoint } from '#/graphql/types';
import useAssetPlanDashboardSingleStatsQuery from '../../hooks/useAssetPlanDashboardSingleStatsQuery';
import useAssetPlanDashboardItems, {
  isChartItemKey,
  isSectionHeadingItem,
  ItemKeys,
} from '../../hooks/useAssetPlanDashboardItems';
import { ASSET_PLAN_COLORS } from '../../constants/chartColors';
import { formatCurrency } from '#/modules/dashboard/utils/formatters';
import { color } from '@nuvolo/nuux-theme/utils';
import styled from 'styled-components';
import AssetPlanSummaryPreview from '../AssetPlanSummaryPreview';

const AssetPlanDashboardItem = styled(DashboardItem)`
  > div {
    background: ${color('contrast', 'dark')} !important;
  }
`;

type DashboardStatsSeries = {
  key: string;
  statsPoints: DashboardStatsPoint[];
};

export interface AssetPlanDashboardProps {
  assetPlanId: string;
  scenarioId: string;
  assetPlan: Pick<AssetPlan, 'name' | 'startYear' | 'endYear' | 'status' | 'notes'>;
}

const sanitizeStatsPoints = (data: unknown): DashboardStatsPoint[] => {
  if (!Array.isArray(data)) return [];

  return data.filter(Boolean) as DashboardStatsPoint[];
};

const sanitizeStatsSeries = (data: unknown): DashboardStatsSeries[] => {
  if (!Array.isArray(data)) return [];

  return data
    .filter(Boolean)
    .map((series: any) => ({
      key: series?.key ? String(series.key) : '',
      statsPoints: sanitizeStatsPoints(series?.statsPoints),
    }))
    .filter((series) => Boolean(series.key));
};

const CURRENCY_KEYS = new Set([
  'totalInterventionCost',
  'priority1Cost',
  'totalReplacementCost',
  'priority1AssetsReplacementCost',
  'findingsTotalInterventionCost',
  'deferredFindingsCost',
]);

const formatStatValue = (
  value: number,
  item: { filterByValue?: string | null },
): string | number => {
  if (item.filterByValue && CURRENCY_KEYS.has(item.filterByValue)) return formatCurrency(value);
  return value.toLocaleString();
};

const AssetPlanDashboard: React.FC<AssetPlanDashboardProps> = ({
  assetPlanId,
  scenarioId,
  assetPlan,
}) => {
  const { items, visibleItems } = useAssetPlanDashboardItems();

  const queries = {
    generalMetrics: useAssetPlanDashboardSingleStatsQuery(
      'general-metrics',
      visibleItems,
      assetPlanId,
      scenarioId,
    ),
    interventionCostByPriority: useAssetPlanDashboardSingleStatsQuery(
      'intervention-cost-by-priority',
      visibleItems,
      assetPlanId,
      scenarioId,
    ),
    assetMetrics: useAssetPlanDashboardSingleStatsQuery(
      'asset-metrics',
      visibleItems,
      assetPlanId,
      scenarioId,
    ),
    interventionCostByAge: useAssetPlanDashboardSingleStatsQuery(
      'intervention-cost-by-age',
      visibleItems,
      assetPlanId,
      scenarioId,
    ),
    replacementCostByAssetType: useAssetPlanDashboardSingleStatsQuery(
      'replacement-cost-by-asset-type',
      visibleItems,
      assetPlanId,
      scenarioId,
    ),
    replacementCostByManufacturer: useAssetPlanDashboardSingleStatsQuery(
      'replacement-cost-by-manufacturer',
      visibleItems,
      assetPlanId,
      scenarioId,
    ),
    findingsMetrics: useAssetPlanDashboardSingleStatsQuery(
      'findings-metrics',
      visibleItems,
      assetPlanId,
      scenarioId,
    ),
    interventionCostAndFci: useAssetPlanDashboardSingleStatsQuery(
      'intervention-cost-and-fci',
      visibleItems,
      assetPlanId,
      scenarioId,
    ),
  };

  const itemRenderers: Record<
    ItemKeys,
    {
      data: any;
      loading: boolean;
      element: ReactElement;
    }
  > = useMemo(() => {
    const interventionCostByPriorityData = sanitizeStatsSeries(
      queries.interventionCostByPriority.data,
    );
    const assetMetricsData = sanitizeStatsPoints(queries.assetMetrics.data);
    const findingsMetricsData = sanitizeStatsPoints(queries.findingsMetrics.data);
    const interventionCostByAgeData = sanitizeStatsPoints(queries.interventionCostByAge.data);
    const replacementCostByAssetTypeData = sanitizeStatsSeries(
      queries.replacementCostByAssetType.data,
    );
    const replacementCostByManufacturerData = sanitizeStatsPoints(
      queries.replacementCostByManufacturer.data,
    );
    const interventionCostAndFciData = sanitizeStatsSeries(queries.interventionCostAndFci.data);

    return {
      'general-metrics': {
        data: queries.generalMetrics.data,
        loading: queries.generalMetrics.loading,
        element: (
          <DashboardCards
            dataSource={queries.generalMetrics.data as DashboardStatsPoint[] | null}
            colors={ASSET_PLAN_COLORS.general}
            formatValue={formatStatValue}
          />
        ),
      },
      'intervention-cost-by-priority': {
        data: interventionCostByPriorityData,
        loading: queries.interventionCostByPriority.loading,
        element: (
          <StackedBarChart
            dataSource={interventionCostByPriorityData}
            noDataText="No intervention cost data available."
            colors={ASSET_PLAN_COLORS.interventionCostByPriority}
            setup={{
              valueAxis: 'Cost ($)',
              argumentAxis: 'Year',
            }}
          />
        ),
      },
      'asset-metrics': {
        data: assetMetricsData,
        loading: queries.assetMetrics.loading,
        element: (
          <DashboardCards
            dataSource={assetMetricsData}
            colors={ASSET_PLAN_COLORS.assetMetrics}
            formatValue={formatStatValue}
          />
        ),
      },
      'intervention-cost-by-age': {
        data: interventionCostByAgeData,
        loading: queries.interventionCostByAge.loading,
        element: (
          <BarChart
            dataSource={interventionCostByAgeData}
            noDataText="No intervention cost by age data available."
            startingDataColor="blue"
            setup={{
              valueAxis: 'Cost ($)',
              argumentAxis: 'Age (years)',
            }}
          />
        ),
      },
      'replacement-cost-by-asset-type': {
        data: replacementCostByAssetTypeData,
        loading: queries.replacementCostByAssetType.loading,
        element: (
          <StackedBarChart
            dataSource={replacementCostByAssetTypeData}
            noDataText="No replacement cost by asset type data available."
            colors={{}}
            setup={{
              valueAxis: 'Cost ($)',
              argumentAxis: 'Intervention Year',
            }}
          />
        ),
      },
      'replacement-cost-by-manufacturer': {
        data: replacementCostByManufacturerData,
        loading: queries.replacementCostByManufacturer.loading,
        element: (
          <PieChart
            type="pie"
            dataSource={replacementCostByManufacturerData}
            startingDataColor="orange"
          />
        ),
      },
      'findings-metrics': {
        data: findingsMetricsData,
        loading: queries.findingsMetrics.loading,
        element: (
          <DashboardCards
            dataSource={findingsMetricsData}
            colors={ASSET_PLAN_COLORS.findingsMetrics}
            formatValue={formatStatValue}
          />
        ),
      },
      'intervention-cost-and-fci': {
        data: interventionCostAndFciData,
        loading: queries.interventionCostAndFci.loading,
        element: (
          <BarLineChart
            dataSource={interventionCostAndFciData}
            noDataText="No intervention cost or FCI data available."
            setup={{
              valueAxis: 'Cost ($)',
              lineAxis: 'FCI (%)',
              argumentAxis: 'Year',
            }}
            barColor="orange"
            lineColor="magenta"
          />
        ),
      },
    };
  }, Object.values(queries));

  return (
    <NuvoGrid container>
      <NuvoGrid item xs={12}>
        <AssetPlanSummaryPreview assetPlan={assetPlan} />
      </NuvoGrid>
      {items
        .filter((item) => item.show)
        .map((item) => {
          if (isSectionHeadingItem(item)) {
            return (
              <DashboardSectionHeading
                key={item.key}
                title={item.title}
                subtitle={item.subtitle}
                {...item.colSpan}
              />
            );
          }

          if (!isChartItemKey(item.key)) return null;

          const { element, loading, data: itemData } = itemRenderers[item.key];

          return (
            <AssetPlanDashboardItem
              key={item.key}
              title={item.title}
              subtitle={item.subtitle}
              showTitle={item.showTitle}
              data={itemData as DashboardStatsPoint[]}
              loading={loading}
              {...item.colSpan}>
              {element}
            </AssetPlanDashboardItem>
          );
        })}
    </NuvoGrid>
  );
};

export default React.memo(AssetPlanDashboard);
